import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SchwabConnection } from '@/components/settings/SchwabConnection';
import { useSyncHistory } from '@/hooks/queries/useSchwab';
import { formatRelativeTime } from '@/lib/formatters';
import toast from 'react-hot-toast';

export function SchwabSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get('error');
    const connected = searchParams.get('connected');

    if (error) {
      toast.error(error);
      setSearchParams({});
    } else if (connected) {
      toast.success('Successfully connected to Schwab!');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Schwab Integration</h1>
        <p className="text-sm text-gray-500 mt-1">
          Connect your Charles Schwab brokerage account
        </p>
      </div>

      <SchwabConnection />
      <SyncHistory />
    </div>
  );
}

function SyncHistory() {
  const { data: logs, isLoading } = useSyncHistory();

  if (isLoading || !logs) return null;
  if (logs.length === 0) return null;

  const statusVariant = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sync Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-3">
                <Badge variant={statusVariant(log.status)} size="sm">
                  {log.status}
                </Badge>
                <span className="text-sm text-gray-700 capitalize">{log.syncType}</span>
                {log.ordersProcessed > 0 && (
                  <span className="text-sm text-gray-500">
                    {log.ordersProcessed} order{log.ordersProcessed !== 1 ? 's' : ''}
                    {log.ordersFailed > 0 && (
                      <span className="text-red-500 ml-1">({log.ordersFailed} failed)</span>
                    )}
                  </span>
                )}
                {log.ordersProcessed === 0 && log.status === 'completed' && (
                  <span className="text-sm text-gray-400">no new orders</span>
                )}
                {log.errorMessage && (
                  <span className="text-sm text-red-500 truncate max-w-[200px]" title={log.errorMessage}>
                    {log.errorMessage}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {formatRelativeTime(log.startedAt)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
